import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Trash2 } from "lucide-react";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import CustomPagination from "../../components/Manual_List/Pagination";
import ItemsPerPageSelect from "../../components/Manual_List/ItemsPerPage";
import { SelectChangeEvent } from "@mui/material/Select";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import { useLocation, useNavigate } from "react-router-dom";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import { SubCategory, Language } from "../../Types/types";
import { useLanguage } from "../../components/LanguageContext";
import subCategoryListTranslations from "./subCategoryListTranslations";
import {
  deleteSubCategory,
  fetchSubCategoriesForList,
} from "../../components/Manual_List/fetch/slices";

const SubcategoriesList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const { language } = useLanguage();
  const t = subCategoryListTranslations[language as Language];
  const { state } = location;
  const { names, id } = state || {};
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [subCategoryToDelete, setSubCategoryToDelete] =
    useState<SubCategory | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(
        fetchSubCategoriesForList({ language, categoryId: parseInt(id) })
      )
        .unwrap()
        .then((data) => {
          setSubcategories(data || []);
        });
    }
  }, [dispatch, id, language]);

  const subCategoriesStatus = useSelector(
    (state: RootState) => state.data.subcategories.status
  );

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const openConfirmationModal = (subCategory: SubCategory) => {
    setSubCategoryToDelete(subCategory);
    setIsConfirmationModalOpen(true);
  };

  const handleNavigateToAddSubCategory = () => {
    navigate(`/add-subcategory/${id}`, {
      state: {
        names,
        categoryId: id,
        subcategories,
      },
    });
  };

  const handleNavigateToEditSubCategory = (subCategoryId: number) => {
    navigate(`/edit-subcategory/${subCategoryId}`, {
      state: {
        subCategoryId,
        subcategories,
        categoryId: id,
      },
    });
  };

  const handleNavigateToCategories = () => {
    navigate(`/edit-category/${id}`, {
      state: {
        names,
      },
    });
  };

  const handleDeleteConfirmation = async () => {
    if (subCategoryToDelete) {
      const resultAction = await dispatch(
        deleteSubCategory(subCategoryToDelete.id)
      );
      if (deleteSubCategory.fulfilled.match(resultAction)) {
        dispatch(
          fetchSubCategoriesForList({ language, categoryId: parseInt(id) })
        )
          .unwrap()
          .then((data) => {
            setSubcategories(data || []);
          });
        setIsConfirmationModalOpen(false);
      }
    }
  };

  if (subCategoriesStatus === "loading") {
    return (
      <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
        <CircularProgress />
      </div>
    );
  }

  const filteredSubCategories =
    subcategories?.filter((subCategory: SubCategory) => {
      const name = subCategory.name;
      return (
        searchTerm === "" ||
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }) || [];

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedSubCategories = filteredSubCategories.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const pageCount = Math.ceil(filteredSubCategories.length / itemsPerPage);

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col">
          <h1 className="text-2xl font-semibold mb-6">
            {t.subCategoryListTitle}
          </h1>

          <div className="mb-6 flex justify-start gap-3">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm dark:bg-white dark:text-black hover:bg-opacity-90 transition-all"
              onClick={handleNavigateToCategories}
            >
              Categories
            </button>
            <button
              type="button"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
            >
              {t.subcategoriesButton}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between items-center gap-4 mb-6 w-full">
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                className="px-4 py-3 border border-gray-300 rounded-lg w-full pr-12 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <SearchIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
            <button
              className="bg-purple text-white px-6 py-2.5 w-full sm:w-48 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              onClick={handleNavigateToAddSubCategory}
            >
              {t.addSubCategoryButton}
            </button>
          </div>

          {paginatedSubCategories.length > 0 && (
            <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-darkSecondaryBackground border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.name}
                    </th>
                    <th className="py-4 px-6 font-semibold text-right text-sm text-gray-700 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-darkSecondaryBackground divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedSubCategories.map((subCategory: SubCategory) => (
                    <tr
                      key={subCategory.id}
                      className="group hover:bg-purple hover:bg-opacity-5 dark:hover:bg-purple dark:hover:bg-opacity-10 transition-all duration-200 cursor-pointer"
                    >
                      <td className="py-4 px-6 text-left text-gray-900 dark:text-gray-100 font-medium">
                        {subCategory.name}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateToEditSubCategory(subCategory.id);
                            }}
                            className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/edit"
                            title="Edit subcategory"
                          >
                            <Pencil
                              className="text-gray-400 dark:text-gray-500 group-hover/edit:text-blue-600 dark:group-hover/edit:text-blue-400 group-hover/edit:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmationModal(subCategory);
                            }}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/delete"
                            title="Delete subcategory"
                          >
                            <Trash2
                              className="text-gray-400 dark:text-gray-500 group-hover/delete:text-red-500 dark:group-hover/delete:text-red-400 group-hover/delete:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {paginatedSubCategories.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-darkSecondaryBackground rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-2">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              <p className="mt-4 text-lg font-medium text-gray-500 dark:text-gray-400">
                No subcategories found
              </p>
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                Try adjusting your search or add a new subcategory
              </p>
            </div>
          )}

          <div className="flex justify-between items-center pt-6 w-full">
            <CustomPagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>
      {subCategoryToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={handleDeleteConfirmation}
          label={t.deleteSubCategoryConfirmation.replace(
            "{label}",
            subCategoryToDelete.name
          )}
        />
      )}
    </div>
  );
};

export default SubcategoriesList;
